/**
 * PATH: packages/ui/src/components/settings/security-tab/TrustedDevicesSection.tsx
 * PURPOSE: List + revoke trusted devices (30-day MFA-skip grants).
 * WHY: Client ask (2026-07): a familiar device bypasses the MFA challenge;
 *      users must be able to SEE which devices hold that grant and pull it
 *      ("tracking option of which devices you are logged into").
 *      Distinct from Active Sessions — a trusted device is an MFA
 *      convenience grant, not a login.
 *
 * FLOW:
 *   ┌──────────────────┐    ┌───────────────┐    ┌───────────────────┐
 *   │ GET /auth/devices│───▶│ display list  │───▶│ revoke one / all  │
 *   └──────────────────┘    └───────────────┘    └───────────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - ../types (SettingsApiClient): API bridge
 */

import { useCallback, useEffect, useState } from 'react'
import { Loader2, ShieldCheck } from 'lucide-react'
import type { SettingsApiClient } from '../types'

export interface TrustedDevice {
  id: number
  name: string
  device: string
  browser: string
  ip: string
  trustedAt: string | null
  expiresAt: string | null
  lastUsedAt: string | null
}

export interface TrustedDevicesSectionProps {
  apiClient: SettingsApiClient
  /** Defaults match the master Django routes (baseURL already has /api). */
  endpoints?: { list?: string; revoke?: string }
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

export function TrustedDevicesSection({ apiClient, endpoints }: TrustedDevicesSectionProps) {
  const listUrl = endpoints?.list || '/auth/devices/'
  const revokeUrl = endpoints?.revoke || '/auth/devices/revoke/'

  const [devices, setDevices] = useState<TrustedDevice[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(listUrl)
      const data = response?.data ?? response
      setDevices(Array.isArray(data?.devices) ? data.devices : [])
    } catch {
      setError('Unable to load trusted devices')
    } finally {
      setLoading(false)
    }
  }, [apiClient, listUrl])

  useEffect(() => {
    void load()
  }, [load])

  const revoke = async (payload: { device_id?: number; all?: boolean }) => {
    setLoading(true)
    setError(null)
    try {
      await apiClient.post(revokeUrl, payload)
      await load()
    } catch {
      setError('Unable to revoke device')
      setLoading(false)
    }
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-muted">
            <ShieldCheck className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Trusted Devices</h3>
            <p className="text-sm text-muted-foreground">
              Devices that skip the two-factor prompt for 30 days after a successful code
            </p>
          </div>
        </div>
        {devices.length > 0 && (
          <button
            onClick={() => void revoke({ all: true })}
            className="text-sm text-destructive hover:text-destructive/80"
          >
            Forget all devices
          </button>
        )}
      </div>

      <div className="space-y-2">
        {loading && (
          <div className="flex items-center justify-center p-6 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading devices...
          </div>
        )}

        {!loading && error && (
          <div className="flex items-center justify-center p-6 text-destructive text-sm">{error}</div>
        )}

        {!loading && !error && devices.length === 0 && (
          <div className="flex items-center justify-center p-6 text-muted-foreground text-sm">
            <p>No trusted devices — you'll be asked for a code on every sign-in</p>
          </div>
        )}

        {!loading && !error && devices.map((device) => (
          <div
            key={device.id}
            className="flex items-center justify-between p-4 rounded-xl border border-border bg-background"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <ShieldCheck className="w-4 h-4 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">{device.name || device.device || 'Device'}</p>
                <p className="text-sm text-muted-foreground">
                  {device.browser || 'Browser'}
                  {device.ip ? ` • ${device.ip}` : ''}
                  {device.expiresAt ? ` • trusted until ${formatDate(device.expiresAt)}` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={() => void revoke({ device_id: device.id })}
              className="text-sm text-destructive hover:text-destructive/80"
            >
              Forget
            </button>
          </div>
        ))}
      </div>
    </section>
  )
}
